import React from 'react';
import { Box, DialogContent, Grid, LinearProgress } from '@mui/material';
import Container from '@mui/material/Container';
import LabelSeparationDialogHeader from '../Common/LabelSeparation/LabelSeparationDialogHeader';
import SectionPageForm from '../Common/SectionMapping/SectionPageForm';
import { SectionMappingMode } from '../Common/SectionMapping/types';
import SignedPdfViewerPane from '../Common/LabelSeparation/SignedPdfViewerPane';
import useBrazilSectionMappingDialog from './useBrazilSectionMappingDialog';
import labelStyles from '../Common/LabelSeparation/labelSeparation.styles';

const DIALOG_TITLES: Record<SectionMappingMode, string> = {
  section: 'Section Page Mapping',
  original: 'Original Page Mapping'
};

const PDF_EMPTY_MESSAGES: Record<SectionMappingMode, string> = {
  section: 'Processed PDF not available. Complete Section Separation first.',
  original: 'Original PDF not available for this document.'
};

type BrazilSectionMappingDialogProps = {
  documentId: number;
  mode: SectionMappingMode;
  registrationNumber?: string;
  onClose: () => void;
  onSaved: () => void;
  /* eslint-disable no-unused-vars -- type-only callback signature */
  onBusyChange?: (busy: boolean) => void;
  /* eslint-enable no-unused-vars */
};

const BrazilSectionMappingDialog = ({
  documentId,
  mode,
  registrationNumber = '',
  onClose,
  onSaved,
  onBusyChange
}: BrazilSectionMappingDialogProps) => {
  const {
    loading,
    saving,
    busy,
    sectionPages,
    qcStatus,
    setQcStatus,
    pdfUrl,
    handleClose,
    handlePageChange,
    handleSave
  } = useBrazilSectionMappingDialog({ documentId, mode, onClose, onSaved, onBusyChange });

  return (
    <Container maxWidth={false}>
      <LabelSeparationDialogHeader
        title={DIALOG_TITLES[mode]}
        subtitle={registrationNumber}
        closeDisabled={busy}
        onClose={handleClose}
      />

      {loading ? (
        <Box sx={labelStyles.loadingBox}>
          <LinearProgress color='secondary' />
        </Box>
      ) : (
        <DialogContent sx={labelStyles.dialogContent}>
          <Grid container spacing={2} sx={labelStyles.gridContainer}>
            <Grid item xs={12} lg={7} sx={labelStyles.pdfColumn}>
              <SignedPdfViewerPane
                loading={loading}
                pdfUrl={pdfUrl}
                height='100%'
                emptyMessage={PDF_EMPTY_MESSAGES[mode]}
              />
            </Grid>
            <Grid item xs={12} lg={5} sx={labelStyles.formColumn}>
              <SectionPageForm
                pages={sectionPages}
                qcStatus={qcStatus}
                saving={saving}
                onPageChange={handlePageChange}
                onQcStatusChange={setQcStatus}
                onSave={handleSave}
                onClose={handleClose}
              />
            </Grid>
          </Grid>
        </DialogContent>
      )}
    </Container>
  );
};

BrazilSectionMappingDialog.defaultProps = {
  registrationNumber: '',
  onBusyChange: undefined
};

export default BrazilSectionMappingDialog;
